import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  UsersIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  PencilIcon,
  TrashIcon,
  KeyIcon,
  UserPlusIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { userManagementApi, invitationApi, roleApi, User, Role, Invitation, Permission } from '@/services/roleApi';
import CreateRoleModal from '@/components/Roles/CreateRoleModal';
import EditRoleModal from '@/components/Roles/EditRoleModal';
import { PermissionGuard } from '@/components/Auth/PermissionGuard';
import Pagination, { paginate } from '@/components/Common/Pagination';

const ITEMS_PER_PAGE = 10;

const UsersPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'users' | 'invitations' | 'roles'>('users');
  const [showInviteUser, setShowInviteUser] = useState(false);
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [invitationSearch, setInvitationSearch] = useState('');
  const [invitationStatusFilter, setInvitationStatusFilter] = useState('');
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [usersPage, setUsersPage] = useState(1);
  const [invitationsPage, setInvitationsPage] = useState(1);
  const [rolesPage, setRolesPage] = useState(1);
  const queryClient = useQueryClient();

  // Queries
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users', userSearch, userRoleFilter],
    queryFn: () => userManagementApi.getAll({
      search: userSearch || undefined,
      roleId: userRoleFilter || undefined,
      limit: 50,
    }).then(res => res.data.data),
  });

  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: () => roleApi.getAll({ limit: 100 }).then(res => res.data.data),
  });

  const { data: invitationsData, isLoading: invitationsLoading } = useQuery({
    queryKey: ['invitations', invitationSearch, invitationStatusFilter],
    queryFn: () => invitationApi.getAll({
      search: invitationSearch || undefined,
      status: invitationStatusFilter || undefined,
      limit: 50,
    }).then(res => res.data.data),
  });

  const { data: userStats } = useQuery({
    queryKey: ['user-stats'],
    queryFn: () => userManagementApi.getStats().then(res => res.data.data),
  });

  const { data: invitationStats } = useQuery({
    queryKey: ['invitation-stats'],
    queryFn: () => invitationApi.getStats().then(res => res.data.data),
  });

  // Mutations
  const inviteUserMutation = useMutation({
    mutationFn: (data: { email: string; roleId: string; message?: string }) => invitationApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      queryClient.invalidateQueries({ queryKey: ['invitation-stats'] });
      toast.success('Invitation sent successfully');
      setShowInviteUser(false);
    },
    onError: () => toast.error('Failed to send invitation'),
  });

  const resendInvitationMutation = useMutation({
    mutationFn: (id: string) => invitationApi.resend(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      toast.success('Invitation resent successfully');
    },
    onError: () => toast.error('Failed to resend invitation'),
  });

  const cancelInvitationMutation = useMutation({
    mutationFn: (id: string) => invitationApi.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      queryClient.invalidateQueries({ queryKey: ['invitation-stats'] });
      toast.success('Invitation cancelled successfully');
    },
    onError: () => toast.error('Failed to cancel invitation'),
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => userManagementApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user-stats'] });
      toast.success('User deleted successfully');
    },
    onError: () => toast.error('Failed to delete user'),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (id: string) => userManagementApi.resetPassword(id),
    onSuccess: () => {
      toast.success('Password reset email sent');
    },
    onError: () => toast.error('Failed to reset password'),
  });

  const createRoleMutation = useMutation({
    mutationFn: (data: { name: string; description?: string; permissions: Permission[] }) => roleApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.success('Role created successfully');
      setShowCreateRole(false);
    },
  });
  
  const updateRoleMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Role> }) => roleApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.success('Role updated successfully');
      setEditingRole(null);
    },
  });
  
  const deleteRoleMutation = useMutation({
    mutationFn: (id: string) => roleApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.success('Role deleted successfully');
    },
  });

  const tabs = [
    { id: 'users', name: 'Users', icon: UsersIcon, count: usersData?.users?.length || 0 },
    { id: 'invitations', name: 'Invitations', icon: EnvelopeIcon, count: invitationStats?.pendingInvitations || 0 },
    { id: 'roles', name: 'Roles', icon: ShieldCheckIcon, count: rolesData?.roles?.length || 0 },
  ];

  const statusColors = {
    pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
    accepted: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
    expired: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    cancelled: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300',
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const isExpiringSoon = (expiresAt: string) => {
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diffDays = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays <= 2 && diffDays > 0;
  };

  useEffect(() => { setUsersPage(1); }, [userSearch, userRoleFilter]);
  useEffect(() => { setInvitationsPage(1); }, [invitationSearch, invitationStatusFilter]);

  const paginatedUsers = paginate(usersData?.users || [], usersPage, ITEMS_PER_PAGE);
  const paginatedInvitations = paginate(invitationsData?.invitations || [], invitationsPage, ITEMS_PER_PAGE);
  const paginatedRoles = paginate(rolesData?.roles || [], rolesPage, ITEMS_PER_PAGE);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Management</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage users, roles, and invitations for your organization
          </p>
        </div>
        <div className="flex space-x-3">
          <PermissionGuard module="invitations" action="create">
            <button
              onClick={() => setShowInviteUser(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <UserPlusIcon className="h-4 w-4 mr-2" />
              Invite User
            </button>
          </PermissionGuard>
          {activeTab === 'roles' && (
            <PermissionGuard module="roles" action="create">
              <button
                onClick={() => setShowCreateRole(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create Role
              </button>
            </PermissionGuard>
          )}
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UsersIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{userStats?.totalUsers || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <EnvelopeIcon className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Pending Invitations</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{invitationStats?.pendingInvitations || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ShieldCheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Roles</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{rolesData?.roles?.filter((r: Role) => r.isActive).length || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <UsersIcon className="h-8 w-8 text-purple-600 dark:text-purple-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{userStats?.activeUsers || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.name}</span>
                  {tab.count > 0 && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      activeTab === tab.id ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'users' && (
            <div className="space-y-4">
              {/* Users Filters */}
              <div className="flex space-x-4">
                <div className="flex-1">
                  <div className="relative">
                    <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search users..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
                    />
                  </div>
                </div>
                <select
                  value={userRoleFilter}
                  onChange={(e) => setUserRoleFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Roles</option>
                  {rolesData?.roles?.map((role: Role) => (
                    <option key={role._id} value={role._id}>{role.name}</option>
                  ))}
                </select>
              </div>

              {/* Users List */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {usersLoading ? (
                    <div className="p-6 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                  ) : (
                    paginatedUsers.map((user: User) => (
                      <div key={user._id} className="p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                            <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{user.name}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{user.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{user.role}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {user.isActive ? 'Active' : 'Inactive'}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <PermissionGuard module="users" action="update">
                              <button
                                onClick={() => resetPasswordMutation.mutate(user._id)}
                                className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                                title="Reset Password"
                              >
                                <KeyIcon className="h-4 w-4" />
                              </button>
                            </PermissionGuard>
                            <PermissionGuard module="users" action="delete">
                              <button
                                onClick={() => {
                                  if (confirm('Are you sure you want to delete this user?')) {
                                    deleteUserMutation.mutate(user._id);
                                  }
                                }}
                                className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                                title="Delete User"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </PermissionGuard>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <Pagination
                currentPage={usersPage}
                totalPages={Math.ceil((usersData?.users?.length || 0) / ITEMS_PER_PAGE)}
                onPageChange={setUsersPage}
                totalItems={usersData?.users?.length || 0}
                itemsPerPage={ITEMS_PER_PAGE}
              />
            </div>
          )}

          {activeTab === 'invitations' && (
            <div className="space-y-4">
              {/* Invitations Filters */}
              <div className="flex space-x-4">
                <div className="flex-1">
                  <div className="relative">
                    <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search invitations..."
                      value={invitationSearch}
                      onChange={(e) => setInvitationSearch(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-blue-500 focus:border-blue-500 w-full"
                    />
                  </div>
                </div>
                <select
                  value={invitationStatusFilter}
                  onChange={(e) => setInvitationStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="accepted">Accepted</option>
                  <option value="expired">Expired</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Invitations List */}
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {invitationsLoading ? (
                    <div className="p-6 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                  ) : (
                    paginatedInvitations.map((invitation: Invitation) => (
                      <div key={invitation._id} className="p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                            <EnvelopeIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{invitation.email}</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Expires: {formatDate(invitation.expiresAt)}
                              {isExpiringSoon(invitation.expiresAt) && (
                                <span className="ml-2 text-orange-600 dark:text-orange-400 font-medium">Expires Soon</span>
                              )}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              statusColors[invitation.status]
                            }`}>
                              {invitation.status}
                            </span>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                              {invitation.roleId}
                            </p>
                          </div>
                          {invitation.status === 'pending' && (
                            <div className="flex items-center space-x-2">
                              <PermissionGuard module="invitations" action="update">
                                <button
                                  onClick={() => resendInvitationMutation.mutate(invitation._id)}
                                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                >
                                  Resend
                                </button>
                              </PermissionGuard>
                              <PermissionGuard module="invitations" action="delete">
                                <button
                                  onClick={() => {
                                    if (confirm('Are you sure you want to cancel this invitation?')) {
                                      cancelInvitationMutation.mutate(invitation._id);
                                    }
                                  }}
                                  className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                                >
                                  Cancel
                                </button>
                              </PermissionGuard>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <Pagination
                currentPage={invitationsPage}
                totalPages={Math.ceil((invitationsData?.invitations?.length || 0) / ITEMS_PER_PAGE)}
                onPageChange={setInvitationsPage}
                totalItems={invitationsData?.invitations?.length || 0}
                itemsPerPage={ITEMS_PER_PAGE}
              />
            </div>
          )}

          {activeTab === 'roles' && (
            <div className="space-y-4">
              {/* Roles List */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {rolesLoading ? (
                  <div className="col-span-full p-6 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </div>
                ) : (
                  paginatedRoles.map((role: Role) => (
                    <div key={role._id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white">{role.name}</h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{role.description}</p>
                          <div className="mt-3">
                            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Permissions:</p>
                            <div className="flex flex-wrap gap-1">
                              {role.permissions.slice(0, 3).map((perm, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300"
                                >
                                  {perm.module}
                                </span>
                              ))}
                              {role.permissions.length > 3 && (
                                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                                  +{role.permissions.length - 3} more
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {role.isSystem && (
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                              System
                            </span>
                          )}
                          {!role.isSystem && (
                            <PermissionGuard module="roles" action="update">
                              <button
                                onClick={() => setEditingRole(role)}
                                className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                                title="Edit role"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            </PermissionGuard>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <Pagination
                currentPage={rolesPage}
                totalPages={Math.ceil((rolesData?.roles?.length || 0) / ITEMS_PER_PAGE)}
                onPageChange={setRolesPage}
                totalItems={rolesData?.roles?.length || 0}
                itemsPerPage={ITEMS_PER_PAGE}
              />
            </div>
          )}
        </div>
      </div>

      {/* Invite User Modal */}
      {showInviteUser && (
        <InviteUserModal
          onClose={() => setShowInviteUser(false)}
          onSubmit={(data) => inviteUserMutation.mutate(data)}
          isLoading={inviteUserMutation.isLoading}
          roles={rolesData?.roles || []}
        />
      )}

      {/* Create Role Modal */}
      {showCreateRole && (
        <CreateRoleModal
          onClose={() => setShowCreateRole(false)}
          onSubmit={(data) => createRoleMutation.mutate(data)}
          isLoading={createRoleMutation.isLoading}
        />
      )}

      {/* Edit Role Modal */}
      {editingRole && (
        <EditRoleModal
          role={editingRole}
          onClose={() => setEditingRole(null)}
          onSubmit={(id, data) => updateRoleMutation.mutate({ id, data })}
          isLoading={updateRoleMutation.isLoading}
        />
      )}
    </div>
  );
};

// Invite User Modal Component
const InviteUserModal: React.FC<{
  onClose: () => void;
  onSubmit: (data: { email: string; roleId: string; message?: string }) => void;
  isLoading: boolean;
  roles: Role[];
}> = ({ onClose, onSubmit, isLoading, roles }) => {
  const [formData, setFormData] = useState({
    email: '',
    roleId: '',
    message: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border dark:border-gray-700 w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Invite User</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role *</label>
            <select
              value={formData.roleId}
              onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
              required
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select a role</option>
              {roles.filter(role => role.isActive).map((role) => (
                <option key={role._id} value={role._id}>
                  {role.name} - {role.description}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Personal Message</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={3}
              placeholder="Add a personal message to the invitation..."
              className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Sending...' : 'Send Invitation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UsersPage;
